import React, { useState, useEffect, useCallback, useRef } from "react";
import { getAuth } from "firebase/auth";
import {
  getDatabase,
  ref,
  set,
  get,
  onValue,
  remove,
  update,
} from "firebase/database";
import "./ProfileScreen.css";

const ProfileScreen = () => {
  const [userId, setUserId] = useState("");
  const [fanpages, setFanpages] = useState([]);
  const [expandedFanpage, setExpandedFanpage] = useState(null);
  const [editingFanpageInfo, setEditingFanpageInfo] = useState({});
  const [contactInfo, setContactInfo] = useState("");
  const [referralPhone, setReferralPhone] = useState("");

  // State cho dropdown "Tôi là"
  const [selectedRole, setSelectedRole] = useState("");
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const roleDropdownRef = useRef(null);

  // Dữ liệu cho dropdown "Tôi là"
  const Roles = {
    "Chủ đầu tư dự án": 1,
    "Người môi giới": 2,
  };

  const [availableFanpages, setAvailableFanpages] = useState([]);
  const [connectedFanpages, setConnectedFanpages] = useState([]);
  const [isLoadingFanpages, setIsLoadingFanpages] = useState(false);
  const [fbInitialized, setFbInitialized] = useState(false);

  const initFacebookSDK = useCallback(() => {
    return new Promise((resolve) => {
      if (window.FB) {
        setFbInitialized(true);
        resolve();
      } else if (document.getElementById("facebook-jssdk")) {
        const checkFBInterval = setInterval(() => {
          if (window.FB) {
            clearInterval(checkFBInterval);
            setFbInitialized(true);
            resolve();
          }
        }, 100);
      } else {
        window.fbAsyncInit = function () {
          window.FB.init({
            appId: process.env.REACT_APP_FB_CLIENT_ID,
            cookie: true,
            xfbml: true,
            version: "v21.0",
          });
          setFbInitialized(true);
          resolve();
        };

        (function (d, s, id) {
          var js,
            fjs = d.getElementsByTagName(s)[0];
          if (d.getElementById(id)) return;
          js = d.createElement(s);
          js.id = id;
          js.src = "https://connect.facebook.net/en_US/sdk.js";
          fjs.parentNode.insertBefore(js, fjs);
        })(document, "script", "facebook-jssdk");
      }
    });
  }, []);

  // Đồng bộ dữ liệu fanpage khi tải từ Facebook API
  useEffect(() => {
    const fbAccessToken = localStorage.getItem("fbAccessToken");
    if (!fbAccessToken) return;

    const fetchFanpages = async () => {
      try {
        await initFacebookSDK();

        // Kiểm tra token trong database trước
        const userId = localStorage.getItem("userId");
        const db = getDatabase();
        const userTokenRef = ref(db, `users/${userId}/fbToken`);
        const tokenSnapshot = await get(userTokenRef);

        let currentToken = fbAccessToken;

        // Nếu có token trong database và chưa hết hạn, sử dụng token đó
        if (tokenSnapshot.exists()) {
          const tokenData = tokenSnapshot.val();
          const now = Date.now();

          if (tokenData.expiresAt && tokenData.expiresAt > now) {
            console.log("Sử dụng token dài hạn từ database");
            currentToken = tokenData.token;

            // Cập nhật token trong localStorage nếu khác
            if (currentToken !== fbAccessToken) {
              localStorage.setItem("fbAccessToken", currentToken);
            }

            // Kiểm tra xem token sắp hết hạn chưa (còn dưới 7 ngày)
            const daysLeft =
              (tokenData.expiresAt - now) / (24 * 60 * 60 * 1000);
            if (daysLeft < 7) {
              console.log(
                `Token sẽ hết hạn trong ${daysLeft.toFixed(
                  1
                )} ngày, cần làm mới`
              );

              try {
                // Thử chuyển đổi token hiện tại thành token dài hạn mới
                const tokenInfo = await exchangeForLongLivedToken(currentToken);

                // Lưu token dài hạn mới
                localStorage.setItem("fbAccessToken", tokenInfo.token);
                await saveTokenToDatabase(userId, tokenInfo);

                // Cập nhật token hiện tại
                currentToken = tokenInfo.token;
                console.log("Đã làm mới token dài hạn thành công");
              } catch (error) {
                console.error("Lỗi khi làm mới token sắp hết hạn:", error);
                // Vẫn sử dụng token hiện tại nếu có lỗi
              }
            }
          } else {
            console.log("Token trong database đã hết hạn, cần làm mới");

            try {
              // Thử chuyển đổi token hiện tại thành token dài hạn
              const tokenInfo = await exchangeForLongLivedToken(fbAccessToken);

              // Lưu token dài hạn mới
              localStorage.setItem("fbAccessToken", tokenInfo.token);
              await saveTokenToDatabase(userId, tokenInfo);

              currentToken = tokenInfo.token;
              console.log("Đã làm mới token hết hạn thành công");
            } catch (error) {
              console.error("Lỗi khi làm mới token:", error);
              // Vẫn sử dụng token hiện tại nếu có lỗi
            }
          }
        } else {
          console.log(
            "Không tìm thấy token trong database, tạo token dài hạn mới"
          );

          try {
            // Thử chuyển đổi token hiện tại thành token dài hạn
            const tokenInfo = await exchangeForLongLivedToken(fbAccessToken);

            // Lưu token dài hạn mới
            localStorage.setItem("fbAccessToken", tokenInfo.token);
            await saveTokenToDatabase(userId, tokenInfo);

            currentToken = tokenInfo.token;
            console.log("Đã tạo token dài hạn mới thành công");
          } catch (error) {
            console.error("Lỗi khi tạo token dài hạn:", error);
            // Vẫn sử dụng token hiện tại nếu có lỗi
          }
        }

        // Sử dụng token hiện tại (có thể là token dài hạn mới hoặc token cũ)
        window.FB.api(
          "/me/accounts",
          "get",
          { access_token: currentToken },
          async function (response) {
            console.log("Kết quả lấy danh sách fanpage:", response);
            if (response && !response.error) {
              // Lưu tất cả fanpage vào state
              setAvailableFanpages(response.data);
              setFanpages(response.data);

              // Đồng bộ fanpage với Firebase
              if (userId) {
                const connectedFanpagesRef = ref(
                  db,
                  `users/${userId}/connectedFanpages`
                );

                // Cập nhật tất cả fanpage vào Firebase
                const updatedFanpages = {};
                response.data.forEach((page) => {
                  updatedFanpages[page.id] = {
                    id: page.id,
                    name: page.name,
                    access_token: page.access_token,
                    category: page.category || "",
                    connectedAt: Date.now(),
                  };
                });

                console.log("Fanpages được cập nhật:", updatedFanpages);

                // Lưu vào database
                await set(connectedFanpagesRef, updatedFanpages);

                // Cập nhật state connectedFanpages
                setConnectedFanpages(
                  response.data.map((page) => ({
                    id: page.id,
                    name: page.name,
                    access_token: page.access_token,
                    category: page.category || "",
                    connectedAt: Date.now(),
                  }))
                );
              }
            } else if (
              response &&
              response.error &&
              response.error.code === 190
            ) {
              // Mã lỗi 190 là lỗi token hết hạn hoặc không hợp lệ
              console.error(
                "Token không hợp lệ hoặc đã hết hạn:",
                response.error
              );

              // Xóa token không hợp lệ
              localStorage.removeItem("fbAccessToken");

              // Xóa token trong database
              if (userId) {
                await remove(userTokenRef);
              }

              alert(
                "Phiên đăng nhập Facebook đã hết hạn. Vui lòng kết nối lại fanpage."
              );
            } else {
              console.error("Lỗi khi lấy danh sách fanpage:", response.error);
            }
          }
        );
      } catch (error) {
        console.error("Lỗi khi lấy thông tin fanpage từ Facebook:", error);
      }
    };

    fetchFanpages();
  }, [initFacebookSDK]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const id = localStorage.getItem("userId");
        setUserId(id);
        const userId = id;
        const db = getDatabase();

        const userRef = ref(db, `users/${userId}/profile`);
        const snapshot = await get(userRef);
        if (snapshot.exists()) {
          const data = snapshot.val();
          setContactInfo(data.contactInfo || "");
          setReferralPhone(data.referralPhone || "");
          setSelectedRole(data.role || "");
          if (data.referralPhone) {
            const input = document.querySelector('input[name="referralPhone"]');
            if (input) input.disabled = true;
          }
        }

        // Lấy danh sách fanpage đã kết nối (thông tin cơ bản)
        const connectedFanpagesRef = ref(
          db,
          `users/${userId}/connectedFanpages`
        );
        const connectedFanpagesSnapshot = await get(connectedFanpagesRef);

        if (connectedFanpagesSnapshot.exists()) {
          const connectedData = connectedFanpagesSnapshot.val();
          const combinedFanpages = await Promise.all(
            Object.values(connectedData).map(async (page) => {
              // Lấy thông tin chi tiết của từng fanpage từ đường dẫn users/{userid}/fanpages/{fanpageid}
              const fanpageDetailRef = ref(
                db,
                `users/${userId}/fanpages/${page.id}`
              );
              const fanpageDetailSnapshot = await get(fanpageDetailRef);
              const pageInfo = fanpageDetailSnapshot.exists()
                ? fanpageDetailSnapshot.val()
                : {};

              console.log(`Fanpage ${page.id} - ${page.name}`, pageInfo); // Log chi tiết pageInfo để debug

              // Kiểm tra chi tiết các trường dữ liệu
              console.log(
                `Fanpage ${page.id} - fanpageInfo:`,
                pageInfo.fanpageInfo
              );
              console.log(`Fanpage ${page.id} - field:`, pageInfo.field);

              // Đảm bảo các trường dữ liệu luôn có giá trị mặc định là chuỗi rỗng nếu không tồn tại
              const fanpageInfo =
                pageInfo && typeof pageInfo.fanpageInfo !== "undefined"
                  ? pageInfo.fanpageInfo
                  : "";
              const field =
                pageInfo && typeof pageInfo.field !== "undefined"
                  ? pageInfo.field
                  : "";

              return {
                ...page,
                // Gán giá trị đã được xử lý
                fanpageInfo,
                field,
                // Lấy tên fanpage từ users/{userid}/fanpages/{fanpageid}/ nếu có
                name: pageInfo.fanpageName || page.name,
              };
            })
          );

          console.log("Combined fanpages:", combinedFanpages); // Log để debug
          setConnectedFanpages(combinedFanpages);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  const handleSaveProfile = async () => {
    try {
      const userId = localStorage.getItem("userId");
      const db = getDatabase();

      const userRef = ref(db, `users/${userId}/profile`);
      const snapshot = await get(userRef);
      const existingData = snapshot.exists() ? snapshot.val() : {};

      if (
        existingData.referralPhone &&
        existingData.referralPhone !== referralPhone
      ) {
        alert("Không thể thay đổi số điện thoại giới thiệu sau khi đã lưu!");
        setReferralPhone(existingData.referralPhone);
        return;
      }

      await update(userRef, {
        contactInfo,
        referralPhone,
        role: selectedRole,
        updatedAt: new Date().toISOString(),
      });

      if (referralPhone && !existingData.referralPhone) {
        const input = document.querySelector('input[name="referralPhone"]');
        if (input) input.disabled = true;
      }

      alert("Lưu thông tin thành công!");
    } catch (error) {
      console.error("Error saving data:", error);
      alert("Lưu thông tin thất bại");
    }
  };

  // Lưu thông tin fanpage
  const handleSaveFanpageInfo = async (fanpageId, fanpageInfo, field) => {
    try {
      const userId = localStorage.getItem("userId");
      const db = getDatabase();

      // Tìm tên fanpage từ danh sách đã kết nối
      const fanpage = connectedFanpages.find((page) => page.id === fanpageId);
      if (!fanpage) {
        alert("Không tìm thấy thông tin fanpage!");
        return false;
      }

      // Đảm bảo dữ liệu không bị undefined
      const safeInfo = typeof fanpageInfo === "string" ? fanpageInfo : "";
      const safeField = typeof field === "string" ? field : "";

      // Lưu vào users/{userid}/fanpages/{fanpageid}/
      const fanpageRef = ref(db, `users/${userId}/fanpages/${fanpageId}`);

      const updatedData = {
        fanpageName: fanpage.name,
        fanpageInfo: safeInfo,
        field: safeField,
        updatedAt: new Date().toISOString(),
      };

      console.log(`Saving fanpage data for ${fanpageId}:`, updatedData);
      await set(fanpageRef, updatedData);

      // Cập nhật state
      setConnectedFanpages((prev) =>
        prev.map((page) =>
          page.id === fanpageId
            ? { ...page, fanpageInfo: safeInfo, field: safeField }
            : page
        )
      );

      // Hiển thị thông báo thành công
      alert("Lưu thông tin fanpage thành công!");

      return true;
    } catch (error) {
      console.error("Error saving fanpage data:", error);
      alert("Lưu thông tin fanpage thất bại: " + error.message);
      return false;
    }
  };

  const handleCopyUserId = () => {
    navigator.clipboard.writeText(userId).then(() => {
      alert("Đã sao chép ID người dùng!");
    });
  };

  // Fix để giải quyết vấn đề hiển thị không đủ fanpage
  // Hàm để chuyển đổi token ngắn hạn thành token dài hạn
  // Tham khảo: https://developers.facebook.com/docs/facebook-login/guides/access-tokens/get-long-lived/
  const exchangeForLongLivedToken = async (shortLivedToken) => {
    try {
      console.log("Đang chuyển đổi token ngắn hạn thành token dài hạn...");

      // Sử dụng API của Facebook để chuyển đổi token ngắn hạn thành token dài hạn
      // Vì không thể gọi trực tiếp từ client do CORS và bảo mật App Secret,
      // chúng ta sẽ sử dụng FB SDK để lấy token dài hạn từ token ngắn hạn

      return new Promise((resolve) => {
        // Sử dụng FB.api để lấy token dài hạn
        window.FB.api(
          "/oauth/access_token",
          "GET",
          {
            grant_type: "fb_exchange_token",
            client_id: process.env.REACT_APP_FB_CLIENT_ID || "",
            client_secret: process.env.REACT_APP_FB_CLIENT_SECRET || "",
            fb_exchange_token: shortLivedToken,
          },
          function (response) {
            if (response && !response.error) {
              console.log("Đã nhận được token dài hạn:", response);

              // Lấy thông tin về token để biết thời gian hết hạn
              window.FB.api(
                "/debug_token",
                "GET",
                {
                  input_token: response.access_token,
                  access_token: shortLivedToken,
                },
                function (debugResponse) {
                  if (debugResponse && !debugResponse.error) {
                    console.log("Thông tin token dài hạn:", debugResponse);

                    // Trả về cả token và thông tin về thời gian hết hạn
                    resolve({
                      token: response.access_token,
                      expiresAt: debugResponse.data.expires_at * 1000, // Chuyển đổi từ giây sang mili giây
                      dataAccessExpiresAt:
                        debugResponse.data.data_access_expires_at * 1000,
                      issuedAt: debugResponse.data.issued_at * 1000,
                      type: debugResponse.data.type,
                      userId: debugResponse.data.user_id,
                    });
                  } else {
                    console.error(
                      "Lỗi khi lấy thông tin token:",
                      debugResponse?.error
                    );
                    // Nếu không lấy được thông tin, vẫn trả về token với thời gian mặc định (60 ngày)
                    resolve({
                      token: response.access_token,
                      expiresAt: Date.now() + 60 * 24 * 60 * 60 * 1000, // 60 ngày tính bằng milliseconds
                    });
                  }
                }
              );
            } else {
              console.error("Lỗi khi lấy token dài hạn:", response?.error);
              // Nếu có lỗi, vẫn sử dụng token ngắn hạn với thời gian mặc định (1 giờ)
              resolve({
                token: shortLivedToken,
                expiresAt: Date.now() + 1 * 60 * 60 * 1000, // 1 giờ tính bằng milliseconds
              });
            }
          }
        );
      });
    } catch (error) {
      console.error("Lỗi khi chuyển đổi token:", error);
      // Nếu có lỗi, vẫn trả về token ngắn hạn với thời gian mặc định (1 giờ)
      return {
        token: shortLivedToken,
        expiresAt: Date.now() + 1 * 60 * 60 * 1000, // 1 giờ tính bằng milliseconds
      };
    }
  };

  // Hàm để lưu token vào Realtime Database
  const saveTokenToDatabase = async (userId, tokenData) => {
    try {
      const db = getDatabase();
      const userTokenRef = ref(db, `users/${userId}/fbToken`);

      // Kiểm tra xem tokenData là object hay string
      if (typeof tokenData === "string") {
        // Nếu là string, tạo object với thông tin mặc định
        await set(userTokenRef, {
          token: tokenData,
          createdAt: Date.now(),
          // Token dài hạn thường có thời hạn 60 ngày
          expiresAt: Date.now() + 60 * 24 * 60 * 60 * 1000, // 60 ngày tính bằng milliseconds
        });
      } else {
        // Nếu là object, lưu toàn bộ thông tin
        const tokenToSave = {
          token: tokenData.token,
          createdAt: Date.now(),
          expiresAt:
            tokenData.expiresAt || Date.now() + 60 * 24 * 60 * 60 * 1000, // Sử dụng giá trị từ tokenData hoặc mặc định 60 ngày
        };

        // Chỉ thêm các trường không phải undefined
        if (tokenData.dataAccessExpiresAt)
          tokenToSave.dataAccessExpiresAt = tokenData.dataAccessExpiresAt;
        if (tokenData.issuedAt) tokenToSave.issuedAt = tokenData.issuedAt;
        if (tokenData.type) tokenToSave.type = tokenData.type;
        if (tokenData.userId) tokenToSave.userId = tokenData.userId;

        await set(userTokenRef, tokenToSave);
      }

      console.log("Đã lưu token vào database");
    } catch (error) {
      console.error("Lỗi khi lưu token vào database:", error);
    }
  };

  // START HELPER FUNCTION
  const fetchAllUserPagesFromFacebook = async (userAccessToken) => {
    const fetchPersonalPages = new Promise((resolve) => {
      window.FB.api(
        "/me/accounts",
        "get",
        {
          access_token: userAccessToken,
          fields: "id,name,access_token,category",
        },
        (res) => {
          if (res && !res.error) {
            resolve(res.data || []);
          } else {
            console.error(
              "Lỗi khi lấy trang cá nhân (/me/accounts):",
              res?.error
            );
            resolve([]); // Trả về mảng rỗng nếu có lỗi để không làm hỏng Promise.all
          }
        }
      );
    });

    const fetchBusinessManagedPages = new Promise((resolve) => {
      window.FB.api(
        "/me/businesses",
        "get",
        {
          access_token: userAccessToken,
          fields: "id,name",
        },
        (res) => {
          if (res && !res.error) {
            const businesses = res.data || [];
            console.log("Danh sách business:", businesses);

            // Nếu không có business, trả về mảng rỗng ngay
            if (!businesses.length) {
              console.log("Không có business nào");
              return resolve([]);
            }

            // Đếm số lượng business để theo dõi khi nào hoàn thành tất cả
            let remainingBusinesses = businesses.length;
            let allBusinessPages = [];

            // Hàm helper để đếm ngược và kiểm tra khi nào hoàn thành tất cả business
            const checkCompletion = () => {
              remainingBusinesses--;
              if (remainingBusinesses <= 0) {
                console.log(
                  `Tổng cộng lấy được ${allBusinessPages.length} trang từ tất cả business`
                );
                resolve(allBusinessPages);
              }
            };

            // Lấy danh sách trang từ mỗi business
            businesses.forEach((business) => {
              let ownedPagesCompleted = false;
              let clientPagesCompleted = false;

              // Hàm kiểm tra khi cả hai loại pages đã được xử lý xong
              const checkBusinessCompletion = () => {
                if (ownedPagesCompleted && clientPagesCompleted) {
                  checkCompletion();
                }
              };

              // Lấy owned_pages
              window.FB.api(
                `/${business.id}/owned_pages`,
                "get",
                {
                  access_token: userAccessToken,
                  fields: "id,name,category,access_token",
                },
                (pagesRes) => {
                  if (pagesRes && !pagesRes.error && pagesRes.data) {
                    console.log(
                      `Lấy được ${pagesRes.data.length} trang từ business ${business.name}:`,
                      pagesRes.data
                    );

                    // Lọc và chỉ giữ các trang có access_token
                    const pagesWithToken = pagesRes.data.filter(
                      (page) => page.access_token
                    );

                    if (pagesWithToken.length < pagesRes.data.length) {
                      console.warn(
                        `Có ${
                          pagesRes.data.length - pagesWithToken.length
                        } trang không có access_token`
                      );
                    }

                    allBusinessPages = allBusinessPages.concat(pagesWithToken);
                  } else {
                    console.error(
                      `Lỗi khi lấy owned_pages từ business ${business.name}:`,
                      pagesRes?.error
                    );
                  }

                  ownedPagesCompleted = true;
                  checkBusinessCompletion();
                }
              );

              // Lấy client_pages
              window.FB.api(
                `/${business.id}/client_pages`,
                "get",
                {
                  access_token: userAccessToken,
                  fields: "id,name,category,access_token",
                },
                (clientPagesRes) => {
                  if (
                    clientPagesRes &&
                    !clientPagesRes.error &&
                    clientPagesRes.data
                  ) {
                    console.log(
                      `Lấy được ${clientPagesRes.data.length} client pages từ business ${business.name}:`,
                      clientPagesRes.data
                    );

                    // Lọc và chỉ giữ các trang có access_token
                    const clientPagesWithToken = clientPagesRes.data.filter(
                      (page) => page.access_token
                    );

                    if (
                      clientPagesWithToken.length < clientPagesRes.data.length
                    ) {
                      console.warn(
                        `Có ${
                          clientPagesRes.data.length -
                          clientPagesWithToken.length
                        } client pages không có access_token`
                      );
                    }

                    allBusinessPages =
                      allBusinessPages.concat(clientPagesWithToken);
                  } else if (clientPagesRes && clientPagesRes.error) {
                    console.error(
                      `Lỗi khi lấy client_pages từ business ${business.name}:`,
                      clientPagesRes.error
                    );
                  }

                  clientPagesCompleted = true;
                  checkBusinessCompletion();
                }
              );
            });
          } else {
            console.error(
              "Lỗi khi lấy danh sách business (/me/businesses):",
              res?.error
            );
            resolve([]); // Trả về mảng rỗng nếu có lỗi
          }
        }
      );
    });

    try {
      const [personalPages, businessPages] = await Promise.all([
        fetchPersonalPages,
        fetchBusinessManagedPages,
      ]);
      const combinedPages = [...personalPages, ...businessPages];

      console.log("Tổng số trang cá nhân:", personalPages.length);
      console.log("Tổng số trang doanh nghiệp:", businessPages.length);
      console.log("Tổng số trang kết hợp:", combinedPages.length);

      const uniquePagesMap = new Map();
      combinedPages.forEach((page) => {
        if (page && page.id && page.name) {
          if (page.access_token) {
            // Chỉ thêm trang có access_token
            if (!uniquePagesMap.has(page.id)) {
              uniquePagesMap.set(page.id, page);
            }
          } else {
            console.warn(
              `Trang ${page.name} (ID: ${page.id}) không có access_token và sẽ bị bỏ qua.`
            );
          }
        }
      });
      return Array.from(uniquePagesMap.values());
    } catch (error) {
      console.error("Lỗi khi tổng hợp danh sách fanpage:", error);
      return []; // Trả về mảng rỗng nếu có lỗi ở Promise.all
    }
  };
  // END HELPER FUNCTION

  const handleConnectNewPages = async () => {
    setIsLoadingFanpages(true);
    try {
      await initFacebookSDK();
      const userId = localStorage.getItem("userId");
      if (!userId) {
        alert("Lỗi: Không tìm thấy ID người dùng.");
        setIsLoadingFanpages(false);
        return;
      }

      window.FB.login(
        function (response) {
          if (response.status === "connected") {
            const shortLivedToken = response.authResponse.accessToken;

            let tokenToUse = shortLivedToken;
            let tokenInfoForStorage = {
              token: shortLivedToken,
              expiresAt: Date.now() + 1 * 60 * 60 * 1000, // Default 1 hour for short-lived
            };

            exchangeForLongLivedToken(shortLivedToken)
              .then((longLivedTokenInfo) => {
                tokenToUse = longLivedTokenInfo.token;
                tokenInfoForStorage = longLivedTokenInfo; // Contains token and expiresAt
                localStorage.setItem("fbAccessToken", tokenToUse);
                return saveTokenToDatabase(userId, tokenInfoForStorage);
              })
              .catch((tokenError) => {
                console.error(
                  "Lỗi khi chuyển đổi hoặc lưu token dài hạn, sử dụng token ngắn hạn:",
                  tokenError
                );
                // Vẫn tiếp tục với token ngắn hạn nếu có lỗi
                localStorage.setItem("fbAccessToken", shortLivedToken); // Lưu token ngắn hạn
                return saveTokenToDatabase(userId, tokenInfoForStorage); // Lưu thông tin token ngắn hạn
              })
              .then(() => {
                return fetchAllUserPagesFromFacebook(tokenToUse);
              })
              .then((allFetchedPages) => {
                if (allFetchedPages.length === 0) {
                  alert(
                    "Không tìm thấy fanpage nào bạn có quyền quản lý hoặc có lỗi xảy ra khi lấy danh sách."
                  );
                  setIsLoadingFanpages(false);
                  return;
                }

                setAvailableFanpages(allFetchedPages);

                const db = getDatabase();
                const connectedFanpagesRef = ref(
                  db,
                  `users/${userId}/connectedFanpages`
                );
                const updatedFanpagesFirebase = {};
                allFetchedPages.forEach((page) => {
                  updatedFanpagesFirebase[page.id] = {
                    id: page.id,
                    name: page.name,
                    access_token: page.access_token,
                    category: page.category || "",
                    connectedAt: Date.now(),
                  };
                });

                return set(connectedFanpagesRef, updatedFanpagesFirebase).then(
                  () => {
                    return Promise.all(
                      allFetchedPages.map((page) => {
                        const fanpageDetailRef = ref(
                          db,
                          `users/${userId}/fanpages/${page.id}`
                        );
                        return get(fanpageDetailRef).then(
                          (fanpageDetailSnapshot) => {
                            const pageInfoDb = fanpageDetailSnapshot.exists()
                              ? fanpageDetailSnapshot.val()
                              : {};
                            const fanpageInfo =
                              typeof pageInfoDb.fanpageInfo === "string"
                                ? pageInfoDb.fanpageInfo
                                : "";
                            const field =
                              typeof pageInfoDb.field === "string"
                                ? pageInfoDb.field
                                : "";
                            return {
                              id: page.id,
                              name: pageInfoDb.fanpageName || page.name,
                              access_token: page.access_token,
                              category: page.category || "",
                              connectedAt:
                                updatedFanpagesFirebase[page.id].connectedAt,
                              fanpageInfo,
                              field,
                            };
                          }
                        );
                      })
                    );
                  }
                );
              })
              .then((detailedFanpages) => {
                if (detailedFanpages) {
                  console.log(
                    "Updated fanpages with details (incl. business):",
                    detailedFanpages
                  );
                  setConnectedFanpages(detailedFanpages);
                  alert("Kết nối fanpage thành công!");
                }
              })
              .catch((pageError) => {
                console.error(
                  "Lỗi khi lấy hoặc xử lý danh sách fanpage:",
                  pageError
                );
                alert(
                  "Không thể lấy hoặc xử lý thông tin fanpage. " +
                    (pageError.message || "")
                );
              })
              .finally(() => {
                setIsLoadingFanpages(false);
              });
          } else {
            setIsLoadingFanpages(false);
            alert("Kết nối Facebook không thành công!");
          }
        },
        {
          scope:
            "pages_manage_posts,pages_read_engagement,pages_show_list,business_management",
          auth_type: "rerequest",
        }
      );
    } catch (error) {
      console.error("Error connecting Facebook pages:", error);
      setIsLoadingFanpages(false);
      alert("Đã xảy ra lỗi khi kết nối với Facebook!");
    }
  };

  const formatExpirationDate = (timestamp) => {
    if (!timestamp) return "Không có";

    let date;
    if (typeof timestamp === "number") {
      if (timestamp > 100000000000) {
        date = new Date(timestamp);
      } else {
        date = new Date(timestamp * 1000);
      }
    } else if (typeof timestamp === "string") {
      date = new Date(timestamp);
    } else if (timestamp instanceof Object && timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      return "Không xác định";
    }

    if (isNaN(date.getTime())) {
      return "Không xác định";
    }

    return date.toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Xử lý click bên ngoài dropdown để đóng dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        roleDropdownRef.current &&
        !roleDropdownRef.current.contains(event.target)
      ) {
        setIsRoleDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [roleDropdownRef]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-2 md:px-4 sm:px-3 md:sm:px-6 md:md:px-8 pt-3 md:pt-6 pb-4 md:pb-8">
        <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-4 md:mb-8 text-center mx-auto">
          Thiết Lập Thông Tin
        </h1>

        <div className="bg-white rounded-lg shadow-md p-3 md:p-6 mb-4 md:mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-3 md:mb-6">
            Thông Tin Cá Nhân
          </h2>
          <div className="mb-3 md:mb-4">
            <label className="block text-gray-700 font-medium mb-1 md:mb-2">
              ID người dùng
            </label>
            <div className="relative">
              <input
                className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-600 pr-12"
                value={userId}
                disabled
                type="text"
              />
              <button
                onClick={handleCopyUserId}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-500 text-white px-3 py-1 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                Sao chép
              </button>
            </div>
          </div>
          <div className="mb-3 md:mb-6">
            <label className="block text-gray-700 font-medium mb-1 md:mb-2">
              Thông tin liên hệ
            </label>
            <textarea
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={contactInfo}
              onChange={(e) => setContactInfo(e.target.value)}
              placeholder="Văn phòng tư vấn BDS Đà Nẵng&#10;- Số điện thoại/zalo: 0123456789&#10;- Địa chỉ: 1234 Trường Chinh, Đà Nẵng"
              rows={4}
            />
            <p className="mt-2 text-sm text-gray-500">
              Thông tin liên hệ này sẽ được hiển thị ở cuối bài viết
            </p>
          </div>

          {/* Dropdown "Tôi là" */}
          {/* <div className="mb-3 md:mb-6">
            <label className="block text-gray-700 font-medium mb-1 md:mb-2">
              Tôi là
            </label>
            <div className="relative" ref={roleDropdownRef}>
              <button
                onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex justify-between items-center font-medium"
                type="button"
              >
                <span className="text-gray-700">
                  {selectedRole || "Chọn vai trò của bạn"}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-gray-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {isRoleDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                  <div className="max-h-60 overflow-y-auto dropdown-content">
                    {Object.keys(Roles).map((role) => (
                      <div
                        key={role}
                        onClick={() => {
                          setSelectedRole(role);
                          setIsRoleDropdownOpen(false);
                        }}
                        className={`px-4 py-3 hover:bg-blue-50 cursor-pointer flex items-center justify-between dropdown-item ${
                          selectedRole === role ? "bg-blue-50" : ""
                        }`}
                      >
                        <span className="font-medium text-gray-800">
                          {role}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Vai trò này sẽ được sử dụng khi tạo bài viết
            </p>
          </div> */}
        </div>

        <div className="bg-white rounded-lg shadow-md p-3 md:p-6 mb-4 md:mb-8">
          <div className="flex justify-between items-center mb-3 md:mb-6">
            <h2 className="text-xl font-semibold text-gray-800">
              Quản Lý Fanpage Facebook
            </h2>
            <button
              onClick={handleConnectNewPages}
              disabled={isLoadingFanpages}
              className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                isLoadingFanpages ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isLoadingFanpages ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Đang kết nối...
                </>
              ) : (
                <>
                  <span className="md:hidden">+ Kết nối</span>
                  <span className="hidden md:inline">
                    + Kết nối Fanpage mới
                  </span>
                </>
              )}
            </button>
          </div>

          <p className="text-gray-600 mb-3 md:mb-4">
            Kết nối fanpage Facebook để sử dụng trong ứng dụng. Bạn có thể thêm
            nhiều fanpage khác nhau từ tài khoản Facebook của mình.
          </p>

          {connectedFanpages.length > 0 ? (
            <div className="space-y-4">
              {connectedFanpages.map((page) => (
                <div
                  key={page.id}
                  className="border border-gray-200 rounded-md p-3 md:p-4 hover:bg-gray-50"
                >
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <h3 className="font-medium text-gray-800 text-lg">
                        {page.name}
                      </h3>
                      <p className="text-gray-500 text-sm">
                        {page.category || "Fanpage Facebook"}
                      </p>
                      <p className="text-gray-500 text-xs mt-1">
                        Kết nối vào:{" "}
                        {new Date(page.connectedAt).toLocaleDateString("vi-VN")}
                      </p>
                      {page.fanpageInfo !== undefined &&
                        page.fanpageInfo !== "" && (
                          <p className="text-gray-700 text-sm mt-1">
                            <span className="font-medium">
                              Thông tin Fanpage:
                            </span>{" "}
                            {page.fanpageInfo}
                          </p>
                        )}
                      {page.field !== undefined && page.field !== "" && (
                        <p className="text-gray-700 text-sm mt-1">
                          <span className="font-medium">Lĩnh vực:</span>{" "}
                          {page.field}
                        </p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <a
                        href={`https://facebook.com/${page.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                          <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                        </svg>
                      </a>
                      {/* <button
                        onClick={() => {
                          // Nếu đang mở rộng fanpage này, đóng lại
                          if (expandedFanpage === page.id) {
                            setExpandedFanpage(null);
                            setEditingFanpageInfo({});
                          } else {
                            // Nếu chưa mở rộng, mở rộng và chuyển sang chế độ chỉnh sửa
                            setExpandedFanpage(page.id);

                            // Đảm bảo các giá trị không bị undefined
                            const fanpageInfo =
                              typeof page.fanpageInfo === "string"
                                ? page.fanpageInfo
                                : "";
                            const field =
                              typeof page.field === "string" ? page.field : "";

                            console.log(`Editing fanpage ${page.id}:`, {
                              fanpageInfo,
                              field,
                            });

                            setEditingFanpageInfo({
                              id: page.id,
                              fanpageInfo,
                              field,
                            });
                          }
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button> */}
                    </div>
                  </div>

                  {expandedFanpage === page.id && (
                    <div className="mt-3 md:mt-4 border-t border-gray-200 pt-3 md:pt-4">
                      <div className="space-y-2 md:space-y-3">
                        <div>
                          <label className="block text-gray-700 text-sm font-medium mb-1">
                            Thông tin Fanpage
                          </label>
                          <textarea
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            value={editingFanpageInfo.fanpageInfo}
                            onChange={(e) => {
                              console.log(
                                "Updating fanpageInfo:",
                                e.target.value
                              );
                              setEditingFanpageInfo({
                                ...editingFanpageInfo,
                                fanpageInfo: e.target.value,
                              });
                            }}
                            placeholder="Thông tin về Fanpage ở cuối bài viết"
                            rows={3}
                          />
                        </div>
                        <div>
                          <label className="block text-gray-700 text-sm font-medium mb-1">
                            Lĩnh vực
                          </label>
                          <input
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            value={editingFanpageInfo.field}
                            onChange={(e) => {
                              console.log("Updating field:", e.target.value);
                              setEditingFanpageInfo({
                                ...editingFanpageInfo,
                                field: e.target.value,
                              });
                            }}
                            placeholder="Thời trang, bất động sản,.."
                          />
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={async () => {
                              console.log("Saving fanpage info:", {
                                id: page.id,
                                fanpageInfo: editingFanpageInfo.fanpageInfo,
                                field: editingFanpageInfo.field,
                              });
                              const success = await handleSaveFanpageInfo(
                                page.id,
                                editingFanpageInfo.fanpageInfo,
                                editingFanpageInfo.field
                              );
                              if (success) {
                                setExpandedFanpage(null);
                                setEditingFanpageInfo({});
                              }
                            }}
                            className="px-2 md:px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                          >
                            Lưu
                          </button>
                          <button
                            onClick={() => {
                              setExpandedFanpage(null);
                              setEditingFanpageInfo({});
                            }}
                            className="px-2 md:px-3 py-1 bg-gray-200 text-gray-800 text-sm rounded-md hover:bg-gray-300"
                          >
                            Hủy
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center p-4 md:p-8 bg-gray-50 rounded-md">
              <p className="text-gray-500">Bạn chưa kết nối fanpage nào</p>
              <p className="text-sm text-gray-400 mt-2">
                Nhấn nút "Kết nối Fanpage mới" để bắt đầu
              </p>
            </div>
          )}

          <div className="mt-3 md:mt-4 p-3 md:p-4 bg-blue-50 rounded-md">
            <h3 className="font-medium text-blue-800 text-sm mb-1 md:mb-2">
              Lưu ý:
            </h3>
            <ul className="text-sm text-blue-700 space-y-1 list-disc pl-5">
              <li>
                Bạn cần là Admin hoặc Editor của fanpage để có thể kết nối
              </li>
              <li>Kết nối fanpage sẽ cho phép ứng dụng đăng bài thay bạn</li>
              <li>Bạn có thể hủy kết nối bất kỳ lúc nào</li>
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-3 md:p-6">
          <button
            className="flex items-center justify-center w-full bg-blue-600 text-white py-2 px-3 md:px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            onClick={handleSaveProfile}
          >
            Lưu thông tin cá nhân
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileScreen;
